import { CdkDrag, CdkDragDrop, CdkDragMove, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import { ViewportRuler } from '@angular/cdk/overlay';
import { AfterViewInit, ChangeDetectorRef, Component, ComponentFactoryResolver, QueryList, ViewChild, ViewChildren, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, combineLatest, EMPTY } from 'rxjs';
import { catchError, map, share, take, tap } from 'rxjs/operators';
import { DashboardComponents } from '../dashboard-cards';
import { DashboardContainerComponent } from '../dashboard-container/dashboard-container.component';
import { DashboardItem } from '../dashboard-item';
import { DashboardOutletDirective } from '../dashboard-outlet.directive';
import { DashboardService } from './dashboard.service';

@Component({
  selector: 'wsg-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardComponent implements AfterViewInit {

  // mixed orientation drag and drop
  @ViewChild(CdkDropListGroup) listGroup: CdkDropListGroup<CdkDropList>;
  @ViewChild(CdkDropList) placeholder: CdkDropList;

  componentContext = this;
  target: CdkDropList;
  targetIndex: number;
  source: CdkDropList;
  sourceIndex: number;
  dragIndex: number;
  activeContainer;
  dashboardItems: DashboardItem[] = []; 
  // end mixed orientation drag and drop

  options = {
    onUpdate: (event: any) => {
      this.dashboardService.sort(this.dashboardItems).pipe(
        take(1)
      ).subscribe();
    }
  };

  disableRipple = true;
  dashboardCards = DashboardComponents.components;

  constructor(private changeDetectorRef: ChangeDetectorRef, private componentFactoryResolver: ComponentFactoryResolver,
              private dashboardService: DashboardService, private snackbar: MatSnackBar, private viewportRuler: ViewportRuler) { 
    this.target = null;
    this.source = null;
  }

  ngAfterViewChecked() {
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit() {
    if (this.placeholder != null) {
      const placeholderElement = this.placeholder.element.nativeElement;

      placeholderElement.style.display = 'none';
      placeholderElement.parentElement.removeChild(placeholderElement);
    }
  }

  processing = new BehaviorSubject(false);
  processing$ = this.processing.asObservable();

  menuItemSelected = new BehaviorSubject<DashboardItem>(null);
  menuItemSelected$ = this.menuItemSelected.asObservable();

  dashboardItemDropped = new BehaviorSubject<CdkDragDrop<DashboardItem[]>>(null);
  dashboardItemDropped$ = this.dashboardItemDropped.asObservable();

  dashboard$ = this.dashboardService.dashboard$.pipe(
    tap(dashboard => {
      this.dashboardItems = dashboard;
    }),
    catchError(error => {
      this.snackbar.open('Error retrieving dashboard', 'DISMISS', { duration: 3000 });
      return EMPTY;
  }));
  
  menu$ = this.dashboardService.menu$.pipe(
    catchError(error => {
      this.snackbar.open('Error retrieving dashboard menu', 'DISMISS', { duration: 3000 });
      return EMPTY;
  }));

  dashboardMenuItemSelected$ = combineLatest([
    this.menu$,
    this.menuItemSelected$
  ]).pipe(
    map(([menu, menuItem]) => {
      if (!menuItem) { return menu; }
      const index = menu.findIndex(m => m.id === menuItem.id);
      menu[index].isSelected = !menu[index].isSelected;
      return menu;
    })
  );

  dashboardComponentsChanged$ = combineLatest([
    this.dashboard$,
    this.menuItemSelected$
  ]).pipe(
    map(([dashboard, menuItem]) => {    
      if (!menuItem) { return dashboard; }
      
      if (!dashboard.find(d => d.component === menuItem.component)) {
        dashboard.push(menuItem);
      } else {
        dashboard.splice(dashboard.findIndex(d => d.component === menuItem.component), 1);
      }

      this.dashboardItems = dashboard;
      return dashboard;
    })
  );

  dashboardComponentsSorted$ = combineLatest([
    this.dashboardComponentsChanged$,
    this.dashboardItemDropped$
  ]).pipe(
    map(([dashboard, event]) => {    
      if (!event) { return dashboard; }

      this.dashboardItems = dashboard;
      return dashboard;
    }),
    share()
  );

  @ViewChildren(DashboardOutletDirective) dashboardOutlet: QueryList<DashboardOutletDirective>;

  loadContent = (template: DashboardOutletDirective, dashboardItem: DashboardItem) => {
    if (!dashboardItem.component) {
      return;
    }

    const viewContainerRef = template.viewContainerRef;
    viewContainerRef.clear();

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.dashboardCards[dashboardItem.component]);
    const componentRef = viewContainerRef.createComponent(componentFactory);
    const instance = componentRef.instance as DashboardContainerComponent;
    instance.dashboardItem = dashboardItem;

    this.changeDetectorRef.detectChanges();
  }

  onDrop(event: CdkDragDrop<DashboardItem[]>) {
    
  }

  returnFalse() {
    return false;
  }

  toggleDashboardComponent(component: DashboardItem) {
    this.processing.next(true);
    if (!component.isSelected) {
      this.dashboardService.put(component)
                           .subscribe(success => {
        this.menuItemSelected.next(component);    
        this.processing.next(false);
      }, error => {
        this.snackbar.open('Error updating dashboard', 'DISMISS', { duration: 3000 });
        this.processing.next(false);
      });
    } else {
      this.dashboardService.delete(component.component)
                           .subscribe(success => {
        this.menuItemSelected.next(component);
        this.processing.next(false);
      }, error => {
        this.snackbar.open('Error updating dashboard', 'DISMISS', { duration: 3000 });
        this.processing.next(false);
      });
    }
  }

  dragMoved(e: CdkDragMove) {
    let point = this.getPointerPositionOnPage(e.event);

    this.listGroup._items.forEach(dropList => {
      if (this.isInsideDropListClientRect(dropList, point.x, point.y)) {
        this.activeContainer = dropList;
        return;
      }
    });
  }

  dropListDropped(event: CdkDragDrop<DashboardItem[]>) {
    if (!this.target) {
      return;
    }

    const placeholderElement = this.placeholder.element.nativeElement;
    const parent = placeholderElement.parentElement;

    placeholderElement.style.display = 'none';

    parent.removeChild(placeholderElement);
    parent.appendChild(placeholderElement);
    parent.insertBefore(this.source.element.nativeElement, parent.children[this.sourceIndex]);

    this.target = null;
    this.source = null;

    if (this.sourceIndex != this.targetIndex) {
      moveItemInArray(this.dashboardItems, this.sourceIndex, this.targetIndex);
    }
  }

  dropListEnterPredicate = ((drag: CdkDrag, drop: CdkDropList) => {
    if (drop === this.placeholder) { 
      return true; 
    }

    if (drop != this.activeContainer) {
      return false;
    }     

    const placeholderElement = this.placeholder.element.nativeElement;
    const sourceElement = drag.dropContainer.element.nativeElement;
    const dropElement = drop.element.nativeElement;

    const dragIndex = this.indexOf(dropElement.parentElement.children, (this.source ? placeholderElement : sourceElement));
    const dropIndex = this.indexOf(dropElement.parentElement.children, dropElement);

    if (!this.source) {
      this.sourceIndex = dragIndex;
      this.source = drag.dropContainer;

      placeholderElement.style.width = sourceElement.clientWidth + 'px';
      placeholderElement.style.height = sourceElement.clientHeight + 'px';
      
      sourceElement.parentElement.removeChild(sourceElement);
    }

    this.targetIndex = dropIndex;
    this.target = drop;

    placeholderElement.style.display = '';
    dropElement.parentElement.insertBefore(placeholderElement, (dropIndex > dragIndex ? dropElement.nextSibling : dropElement));

    this.placeholder._dropListRef.enter(drag._dragRef, drag.element.nativeElement.offsetLeft, drag.element.nativeElement.offsetTop);
    return false;
  }).bind(this);
  
  /** Determines the point of the page that was touched by the user. */
  getPointerPositionOnPage(event: MouseEvent | TouchEvent) {
    // `touches` will be empty for start/end events so we have to fall back to `changedTouches`.
    const point = this.isTouchEvent(event) ? (event.touches[0] || event.changedTouches[0]) : event;
    const scrollPosition = this.viewportRuler.getViewportScrollPosition();

    return {
      x: point.pageX - scrollPosition.left,
      y: point.pageY - scrollPosition.top
    };
  } 

  indexOf(collection, node) {
    return Array.prototype.indexOf.call(collection, node);
  }

  isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
    return event.type.startsWith('touch');
  }
  
  isInsideDropListClientRect(dropList: CdkDropList, x: number, y: number) {
    const {top, bottom, left, right} = dropList.element.nativeElement.getBoundingClientRect();
    return y >= top && y <= bottom && x >= left && x <= right; 
  }
}